import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Toaster } from "sonner";

// Core (kept in main bundle) — the fastest-first paint path.
import Navbar from "@/components/Navbar";
import BetaBanner from "@/components/BetaBanner";
import ProtectedRoute from "@/components/ProtectedRoute";
import InstallPrompt from "@/components/InstallPrompt";
import CookieBanner from "@/components/CookieBanner";
import WhatsAppFloat from "@/components/WhatsAppFloat";import ChatBotFloat from "@/components/ChatBotFloat";
import GlobalFooter from "@/components/GlobalFooter";
import ScrollToTop from "@/components/ScrollToTop";
import Landing from "@/pages/Landing";
import { AuthProvider } from "@/context/AuthContext";
import { captureAttribution } from "@/lib/attribution";
import useBranding from "@/lib/useBranding";

// V2.13 — Lazy-loaded chunks. Admin + heavy catalogue pages are pulled on
// demand to shrink the initial landing bundle.
const Simulator = lazy(() => import("@/pages/Simulator"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const LegalPages = lazy(() => import("@/pages/LegalPages"));
const ReviewsPage = lazy(() => import("@/pages/ReviewsPage"));
const CataloguePublic = lazy(() => import("@/pages/CataloguePublic"));
const CatalogueProductPage = lazy(() => import("@/pages/CatalogueProductPage"));
const Subsidies = lazy(() => import("@/pages/Subsidies"));
const QuoteView = lazy(() => import("@/pages/QuoteView"));
const PaymentSimulator = lazy(() => import("@/pages/PaymentSimulator"));
const NewsletterPages = lazy(() => import("@/pages/NewsletterPages"));

// Auth
const Login = lazy(() => import("@/pages/Login"));
const Signup = lazy(() => import("@/pages/Signup"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Account = lazy(() => import("@/pages/Account"));
const OAuthSuccess = lazy(() => import("@/pages/OAuthSuccess"));
const MfaVerify = lazy(() => import("@/pages/MfaVerify"));
const MfaSetup = lazy(() => import("@/pages/MfaSetup"));

// Admin — all lazy (never needed on public traffic).
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminLeads = lazy(() => import("@/pages/AdminLeads"));
const AdminLeadDetail = lazy(() => import("@/pages/AdminLeadDetail"));
const AdminCatalogue = lazy(() => import("@/pages/AdminCatalogue"));
const AdminSubsidies = lazy(() => import("@/pages/AdminSubsidies"));
const AdminMessages = lazy(() => import("@/pages/AdminMessages"));
const AdminBranding = lazy(() => import("@/pages/AdminBranding"));
const AdminWhitelabel = lazy(() => import("@/pages/AdminWhitelabel"));
const AdminInvoices = lazy(() => import("@/pages/AdminInvoices"));
const AdminReviews = lazy(() => import("@/pages/AdminReviews"));
const AdminAnalytics = lazy(() => import("@/pages/AdminAnalytics"));
const AdminForecast = lazy(() => import("@/pages/AdminForecast"));

function RouteFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
    </div>
  );
}

// Small wrappers to expose the named exports from LegalPages / NewsletterPages
// as default-like lazy chunks.
const PrivacyPage = lazy(async () => ({ default: (await import("@/pages/LegalPages")).PrivacyPage }));
const CookiesPage = lazy(async () => ({ default: (await import("@/pages/LegalPages")).CookiesPage }));
const TermsPage = lazy(async () => ({ default: (await import("@/pages/LegalPages")).TermsPage }));
const NewsletterConfirmed = lazy(async () => ({ default: (await import("@/pages/NewsletterPages")).NewsletterConfirmed }));
const NewsletterError = lazy(async () => ({ default: (await import("@/pages/NewsletterPages")).NewsletterError }));

function App() {
  const { i18n } = useTranslation();
  useBranding();
  useEffect(() => {
    captureAttribution(i18n.resolvedLanguage || i18n.language);
    if (typeof window !== "undefined" && "serviceWorker" in navigator) {
      window.addEventListener("load", () => {
        navigator.serviceWorker.register("/service-worker.js").catch(() => {});
      });
    }
  }, [i18n]);
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <BetaBanner />
          <Navbar />
          <main>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/simulateur" element={<Simulator />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/cookies" element={<CookiesPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/reviews" element={<ReviewsPage />} />
                <Route path="/newsletter/confirmed" element={<NewsletterConfirmed />} />
                <Route path="/newsletter/error" element={<NewsletterError />} />
                <Route path="/catalogue" element={<CataloguePublic />} />
                <Route path="/catalogue/:category/:slug" element={<CatalogueProductPage />} />
                {/* V2.12.2 — Public subsidies page (accepts both FR/NL slugs) */}
                <Route path="/subventions" element={<Subsidies />} />
                <Route path="/subsidies" element={<Subsidies />} />
                <Route path="/subsidies-benelux" element={<Subsidies />} />
                <Route path="/devis/:quoteNumber" element={<QuoteView />} />
                <Route path="/payment-simulator" element={<PaymentSimulator />} />
                {/* V2.3 — Auth (customer) */}
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/login/oauth-success" element={<OAuthSuccess />} />
                {/* V2.10 — MFA */}
                <Route path="/mfa-verify" element={<MfaVerify />} />
                <Route path="/mfa-setup" element={<MfaSetup />} />
                <Route path="/account/*" element={<Account />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/messages" element={<ProtectedRoute><AdminMessages /></ProtectedRoute>} />
                <Route path="/admin/branding" element={<ProtectedRoute><AdminBranding /></ProtectedRoute>} />
                <Route path="/admin/whitelabel" element={<ProtectedRoute><AdminWhitelabel /></ProtectedRoute>} />
                <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />
                <Route path="/admin/forecast" element={<ProtectedRoute><AdminForecast /></ProtectedRoute>} />
                <Route path="/admin/invoices" element={<ProtectedRoute><AdminInvoices /></ProtectedRoute>} />
                <Route path="/admin/reviews" element={<ProtectedRoute><AdminReviews /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/leads" element={<ProtectedRoute><AdminLeads /></ProtectedRoute>} />
                <Route path="/admin/leads/:id" element={<ProtectedRoute><AdminLeadDetail /></ProtectedRoute>} />
                <Route path="/admin/catalogue" element={<ProtectedRoute><AdminCatalogue /></ProtectedRoute>} />
                <Route path="/admin/subsidies" element={<ProtectedRoute><AdminSubsidies /></ProtectedRoute>} />
              </Routes>
            </Suspense>
          </main>
          <GlobalFooter />
          <InstallPrompt />
          <CookieBanner />
          <WhatsAppFloat />
          <ChatBotFloat />
          {/* V2.16 — Sonner toast portal (mounted once at the app root).
              Each toast root gets the class `app-toast` so Playwright has a
              stable selector: `[data-sonner-toast].app-toast`. Flow-specific
              toasts (ex: AI apply) layer their own class on top. */}
          <Toaster
            position="top-right"
            richColors
            closeButton
            duration={4000}
            toastOptions={{
              className: "app-toast",
              classNames: { toast: "font-sans" },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
